import { Fragment, type ReactNode } from 'react'
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface DataTableColumn<T> {
  key: string
  label: string
  sortable?: boolean
  align?: 'left' | 'right' | 'center'
  render?: (row: T) => ReactNode
  width?: string
  className?: string
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  rows: T[]
  sortBy?: string
  sortDir?: 'asc' | 'desc'
  onSort?: (key: string) => void
  onRowClick?: (row: T) => void
  getRowId: (row: T) => string | number
  selection?: {
    selected: Set<string | number>
    onToggle: (id: string | number) => void
    onToggleAll?: () => void
  }
  expandable?: {
    isExpanded: (row: T) => boolean
    onToggle: (row: T) => void
    renderExpanded: (row: T) => ReactNode
  }
  loading?: boolean
  emptyMessage?: string
  colSpanAll?: number
}

export default function DataTable<T>({
  columns,
  rows,
  sortBy,
  sortDir,
  onSort,
  onRowClick,
  getRowId,
  selection,
  expandable,
  loading,
  emptyMessage = 'Keine Daten',
}: DataTableProps<T>) {
  const totalCols = columns.length + (selection ? 1 : 0) + (expandable ? 1 : 0)
  return (
    <div className="overflow-x-auto rounded-md border border-[var(--border)]">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--border)] bg-[var(--muted)]">
            {expandable && <th className="w-8 px-2 py-2" />}
            {selection && (
              <th className="w-10 px-3 py-2">
                <input
                  type="checkbox"
                  checked={rows.length > 0 && selection.selected.size === rows.length}
                  onChange={selection.onToggleAll}
                  className="rounded"
                />
              </th>
            )}
            {columns.map((col) => {
              const isActive = sortBy === col.key
              const canSort = !!col.sortable && !!onSort
              const alignCls = col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
              return (
                <th
                  key={col.key}
                  style={col.width ? { width: col.width } : undefined}
                  className={cn('px-3 py-2 font-medium', alignCls, canSort && 'cursor-pointer select-none hover:text-[var(--foreground)]', col.className)}
                  onClick={canSort ? () => onSort?.(col.key) : undefined}
                >
                  <span className={cn('inline-flex items-center gap-1', col.align === 'right' && 'justify-end')}>
                    {col.label}
                    {canSort && (
                      isActive && sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> :
                      isActive && sortDir === 'desc' ? <ArrowDown className="h-3 w-3" /> :
                      <ArrowUpDown className="h-3 w-3 text-[var(--muted-foreground)]" />
                    )}
                  </span>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={totalCols} className="py-12 text-center text-sm text-[var(--muted-foreground)]">Laden...</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={totalCols} className="py-12 text-center text-sm text-[var(--muted-foreground)]">{emptyMessage}</td></tr>
          ) : rows.map((row) => {
            const id = getRowId(row)
            const expanded = expandable?.isExpanded(row)
            return (
              <Fragment key={String(id)}>
                <tr
                  className={cn(
                    'border-b border-[var(--border)] hover:bg-[var(--muted)] transition-colors',
                    onRowClick && 'cursor-pointer',
                  )}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {expandable && (
                    <td className="px-2 py-2" onClick={(e) => { e.stopPropagation(); expandable.onToggle(row) }}>
                      <button className="rounded p-0.5 hover:bg-[var(--accent)]" aria-label={expanded ? 'Zuklappen' : 'Aufklappen'}>
                        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </td>
                  )}
                  {selection && (
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selection.selected.has(id)}
                        onChange={() => selection.onToggle(id)}
                        className="rounded"
                      />
                    </td>
                  )}
                  {columns.map((col) => {
                    const alignCls = col.align === 'right' ? 'text-right tabular-nums' : col.align === 'center' ? 'text-center' : 'text-left'
                    const cell = col.render ? col.render(row) : String((row as Record<string, unknown>)[col.key] ?? '—')
                    return (
                      <td key={col.key} className={cn('px-3 py-2', alignCls, col.className)}>
                        {cell}
                      </td>
                    )
                  })}
                </tr>
                {expandable && expanded && (
                  <tr className="bg-[var(--muted)]/30">
                    <td colSpan={totalCols} className="px-4 py-3">
                      {expandable.renderExpanded(row)}
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
